import { authInfo } from '../ducks/auth.duck';
import { fetchCurrentUser } from '../ducks/user.duck';
import { completeAuth0FlexSession } from './api';
import { ensureCurrentUser } from './data';
import * as log from './log';
import { getUserTypeDefaultRedirectPath } from './userTypeRedirects';

/**
 * Exchange Auth0 ID token for Flex session cookies and navigate (shared by Auth0FlexBridge
 * and in-app Passwordless OTP flow).
 *
 * @param {object} params
 * @param {string} params.idToken
 * @param {string} [params.from]
 * @param {string} [params.userType]
 * @param {string} [params.defaultConfirm]
 * @param {import('redux').Dispatch} params.dispatch
 * @param {import('react-router-dom').History} params.history
 * @param {import('redux').Store} params.store
 * @param {object[]} params.routeConfiguration
 * @returns {Promise<{ ok?: boolean, needsConfirm?: boolean, defaultConfirm?: string }>}
 */
export async function completeAuth0FlexSessionAndNavigate({
  idToken,
  from,
  userType,
  defaultConfirm,
  dispatch,
  history,
  store,
  routeConfiguration,
}) {
  log.navigationDiagnostic('auth0_flex_bridge_app_state', {
    hasFrom: Boolean(from && String(from).length > 0),
    fromPathPreview: log.safePathPreviewForLog(from),
    hasUserType: Boolean(userType),
    hasDefaultConfirm: Boolean(defaultConfirm),
    source: 'flex_session_navigate',
  });

  const result = await completeAuth0FlexSession({
    idToken,
    ...(from ? { from } : {}),
    ...(userType ? { userType } : {}),
    ...(defaultConfirm ? { defaultConfirm } : {}),
  });

  if (result.needsConfirm) {
    const confirmPath = defaultConfirm || result.defaultConfirm || '/confirm';
    log.navigationDiagnostic('auth0_flex_bridge_navigate', {
      targetKind: 'confirm',
      pathPreview: log.safePathPreviewForLog(confirmPath),
    });
    history.push(confirmPath);
    return result;
  }

  if (result.ok) {
    await dispatch(authInfo());
    await dispatch(fetchCurrentUser({ afterLogin: true, enforce: true }));
    const userState = store.getState().user;
    const cu = userState?.currentUser;
    // fetchCurrentUser swallows 401/403; do not treat a stub user as logged in.
    if (userState?.currentUserShowError || !cu?.id) {
      const failed = new Error('unexpected_flex_response');
      failed.error = 'unexpected_flex_response';
      log.error(failed, 'auth0-flex-session-user-missing', {
        hasCurrentUserShowError: Boolean(userState?.currentUserShowError),
        hasCurrentUserId: Boolean(cu?.id),
      });
      throw failed;
    }
    const user = ensureCurrentUser(cu);

    const returnPath =
      from && String(from).length > 0
        ? from
        : (() => {
            const ut = user?.attributes?.profile?.publicData?.userType;
            return getUserTypeDefaultRedirectPath(ut, user, routeConfiguration);
          })();

    log.navigationDiagnostic('auth0_flex_bridge_navigate', {
      targetKind: from && String(from).length > 0 ? 'from' : 'default',
      pathPreview: log.safePathPreviewForLog(returnPath),
    });
    history.push(returnPath);
    return result;
  }

  const unexpected = new Error('unexpected_flex_response');
  unexpected.error = 'unexpected_flex_response';
  log.error(unexpected, 'auth0-flex-session-unexpected', { result });
  throw unexpected;
}
